<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.tasks') }}</h2>
      <el-button @click="load" :loading="loading">{{ $t('common.refresh') }}</el-button>
    </div>

    <div class="glass-card" style="margin-top:14px; padding:14px;">
      <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
        <el-select v-model="receiptStatus" :placeholder="$t('field.status')" clearable style="width: 220px;">
          <el-option :label="$t('status.task.UNREAD')" value="UNREAD" />
          <el-option :label="$t('status.task.READ')" value="READ" />
          <el-option :label="$t('status.task.SUBMITTED')" value="SUBMITTED" />
        </el-select>
      </div>

      <el-table :data="items" style="width:100%; margin-top:12px;" @row-click="open">
        <el-table-column prop="taskId" label="ID" width="90" />
        <el-table-column prop="title" :label="$t('field.title')" />
        <el-table-column prop="receiptStatus" :label="$t('field.status')" width="140">
          <template slot-scope="s">
            <span class="tag-pill">{{ $t(`status.task.${displayStatus(s.row)}`) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="190" />
      </el-table>
      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-drawer
      :title="$t('menu.tasks')"
      :visible.sync="drawer"
      size="70%"
      :with-header="true"
      :modal="false"
      :append-to-body="false"
    >
      <div style="padding:16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ current ? current.title : '-' }}</div>
          <span class="tag-pill">{{ current ? $t(`status.task.${displayStatus(current)}`) : '-' }}</span>
        </div>

        <div class="muted" style="margin-top:6px;">{{ current ? current.createdAtLabel : '-' }}</div>
        <div class="content" v-if="current">{{ current.content }}</div>

        <div v-if="current && current.fileIds && current.fileIds.length>0" class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('field.attachments') }}</div>
          <div style="margin-top:8px;">
            <AttachmentGallery :file-ids="current.fileIds" />
          </div>
        </div>

        <div v-if="current && current.receiptStatus==='SUBMITTED'" class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('task.receipt') }}</div>
          <div class="muted" v-if="!current.receiptContent" style="margin-top:8px;">-</div>
          <div v-else style="margin-top:8px;white-space:pre-wrap;line-height:1.6;">{{ current.receiptContent }}</div>
          <div v-if="current.receiptFileIds && current.receiptFileIds.length>0" style="margin-top:10px;">
            <AttachmentGallery :file-ids="current.receiptFileIds" />
          </div>
        </div>

        <div style="margin-top:12px; display:flex; gap:10px; flex-wrap:wrap;">
          <el-button v-if="current && !current.confirmedAt" type="primary" class="btn-glass" :loading="actionLoading" @click="confirm">
            {{ $t('common.confirm') }}
          </el-button>
          <el-button v-if="current && current.taskStatus!=='CLOSED' && current.confirmedAt && current.receiptStatus!=='SUBMITTED'" type="primary" class="btn-glass" :loading="actionLoading" @click="receiptDialog=true">
            {{ $t('common.submit') }}
          </el-button>
          <span v-if="current && current.taskStatus==='CLOSED'" class="tag-pill tag-pill--warn">{{ $t('admin.taskClosed') }}</span>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      :title="$t('task.receipt')"
      :visible.sync="receiptDialog"
      width="520px"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onReceiptClose"
    >
      <el-input v-model="receiptContent" type="textarea" :rows="6" />
      <div style="margin-top:10px;">
        <FileUploader ref="receiptUploader" v-model="receiptFiles" @uploading-change="receiptUploading = $event" />
      </div>
      <span slot="footer">
        <el-button @click="receiptDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="actionLoading || receiptUploading"
          :disabled="actionLoading || receiptUploading"
          @click="submitReceipt"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { secondaryPageTasks, secondaryTaskRead, secondaryTaskConfirm, secondaryTaskReceipt } from '../../api/secondary';
import FileUploader from '../../components/FileUploader.vue';
import AttachmentGallery from '../../components/AttachmentGallery.vue';

export default {
  name: 'SecondaryTaskList',
  components: { FileUploader, AttachmentGallery },
  data() {
    return {
      loading: false,
      actionLoading: false,
      receiptStatus: '',
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      drawer: false,
      current: null,
      receiptDialog: false,
      receiptContent: '',
      receiptFiles: [],
      receiptUploading: false
    };
  },
  created() {
    this.load();
  },
  computed: {
    version() {
      return this.$store.state.versions.tasks;
    },
    receiptFileIds() {
      return (this.receiptFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    }
  },
  watch: {
    version() {
      this.load();
    },
    receiptStatus() {
      this.page = 1;
      this.load();
    }
  },
  methods: {
    displayStatus(row) {
      if (!row) return 'UNREAD';
      if (row.receiptStatus === 'SUBMITTED') return 'SUBMITTED';
      if (row.confirmedAt) return 'CONFIRMED';
      return row.receiptStatus || 'UNREAD';
    },
    onReceiptClose() {
      try {
        if (this.$refs.receiptUploader && this.$refs.receiptUploader.cancelUploads) this.$refs.receiptUploader.cancelUploads();
      } catch (e) {}
      this.receiptContent = '';
      this.receiptFiles = [];
      this.receiptUploading = false;
    },
    async load() {
      this.loading = true;
      try {
        const r = await secondaryPageTasks({ receiptStatus: this.receiptStatus || undefined, page: this.page - 1, size: this.pageSize });
        this.items = (r.items || []).map((x) => ({
          ...x,
          createdAtLabel: x.createdAt ? new Date(x.createdAt).toLocaleString() : '-'
        }));
        this.total = r.total || 0;
        this.$store.commit('setSecondaryBadges', {
          ...(this.$store.state.secondaryBadges || {}),
          taskRedDot: r.redDotCount || 0
        });
        this.syncCurrent();
      } finally {
        this.loading = false;
      }
    },
    syncCurrent() {
      if (!this.current) return;
      const found = (this.items || []).find((x) => x.taskId === this.current.taskId);
      if (found) this.current = found;
    },
    async open(row) {
      if (!row || !row.taskId) return;
      this.current = row;
      this.drawer = true;
      try {
        await secondaryTaskRead(row.taskId);
        await this.load();
      } catch (e) {}
    },
    async confirm() {
      if (!this.current) return;
      this.actionLoading = true;
      try {
        await secondaryTaskConfirm(this.current.taskId);
        await this.load();
        this.$message.success(this.$t('common.confirm'));
      } finally {
        this.actionLoading = false;
      }
    },
    async submitReceipt() {
      if (!this.current) return;
      if (!this.receiptContent) return this.$message.error(this.$t('task.requiredContent'));
      try {
        if (this.$refs.receiptUploader && this.$refs.receiptUploader.isUploading && this.$refs.receiptUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.actionLoading = true;
      try {
        await secondaryTaskReceipt(this.current.taskId, { content: this.receiptContent, fileIds: this.receiptFileIds });
        try {
          if (this.$refs.receiptUploader && this.$refs.receiptUploader.markCommitted) this.$refs.receiptUploader.markCommitted(this.receiptFileIds);
        } catch (e) {}
        this.receiptDialog = false;
        this.onReceiptClose();
        await this.load();
      } finally {
        this.actionLoading = false;
      }
    },
    onPageChange(p) {
      this.page = p;
      this.load();
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
      this.load();
    }
  }
};
</script>

<style scoped>
.content {
  white-space: pre-wrap;
  margin-top: 10px;
  color: rgba(30, 41, 59, 0.95);
  line-height: 1.6;
}
</style>
